import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { Translate } from '../../../../../../../shared/LanguageService';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { useEditedImageQuery } from '../../imagePreview/queries/use-query-edited-image';
import { getImageComments } from '../state/utils';
import { ImageCommentList } from './ImageCommentList';

const TitleBar = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingRight: '8px'
});

const Title = styled(DialogTitle)({
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
});

const Comments = styled(DialogContent)({
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
});

type ImageCommentsDialogProps = {
    orderId: number;
    imageId: number | null;
    filename: string;
    user: AuthenticatedUser;
    onClose: () => void;
};

export function ImageCommentsDialog({
    orderId,
    imageId,
    filename,
    user,
    onClose
}: ImageCommentsDialogProps) {
    let image = useEditedImageQuery({ orderId, imageId, user });
    let comments = image.data ? getImageComments(image.data.imageShowcaseViewModels) : [];

    return (
        <Dialog
            open={imageId !== null}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="image-comments-dialog-title"
        >
            <TitleBar>
                <Title id="image-comments-dialog-title">
                    {`${Translate('image-processing/modal/comments/header-label/comments')}: ${filename}`}
                </Title>
                <IconButton
                    aria-label={Translate('image-processing/modal/footer/button-label/close')}
                    onClick={onClose}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </TitleBar>
            <Comments aria-busy={image.isLoading}>
                {image.isLoading ? (
                    <Skeleton variant="rounded" animation="wave" height={96} />
                ) : (
                    <ImageCommentList comments={comments} />
                )}
            </Comments>
        </Dialog>
    );
}
